import BuyCreditsForm from "@/elements/components/buy-credits/buy-credits-form";
import Loader from "@/elements/components/shared/loader";
import { AppBar } from "@/elements/components/ui/app-bar";
import { api } from "@/elements/components/utils/convex-api";
import goBack from "@/elements/components/utils/go-back";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import Toast from "react-native-toast-message";

function BuyCreditsScreen() {
  const { data: childList = [], isLoading: isLoadingChildren } = useQuery(
    convexQuery(api.parent.buyCredits.queries.getMyChildren, {})
  );
  const studentIds = useMemo(
    () => childList.map((child) => child._id),
    [childList]
  );
  const [firstChild] = childList;
  const firstChildId = firstChild?._id;
  const [selectedChildId, setSelectedChildId] = useState<Id<"students"> | null>(
    null
  );
  const { success, returnTo, bookingData } = useLocalSearchParams<{ 
    success: string;
    returnTo: string;
    bookingData: string;
  }>();
  const router = useRouter();

  const { data: lessonPrices } = useQuery(
    convexQuery(
      api.parent.buyCredits.queries.getLessonPricesForStudents,
      studentIds.length > 0
        ? {
            studentIds: studentIds,
          }
        : "skip"
    )
  );

  useEffect(() => {
    if (firstChildId && !selectedChildId) {
      setSelectedChildId(firstChildId);
    }
  }, [firstChildId, selectedChildId]);

  useEffect(() => {
    if (success === "true") {
      Toast.show({
        type: "success",
        text1: "Credits purchased successfully",
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
        onHide: () => {
          if (returnTo === "booking" && bookingData) {
            try {
              const decodedBookingData = JSON.parse(decodeURIComponent(bookingData));
              const bookingParams = new URLSearchParams(decodedBookingData).toString();
              router.replace(`/(app)/booking/lesson-request?${bookingParams}`);
            } catch (error) {
              console.error("Failed to decode booking data:", error);
              router.replace("/(app)/buy-credits");
            }
          } else {
            router.replace("/(app)/buy-credits");
          }
        },
      });
    } else if (success === "false") {
      Toast.show({
        type: "error",
        text1: "Credits purchase failed",
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
        onHide: () => {
          if (returnTo === "booking" && bookingData) {
            try {
              const decodedBookingData = JSON.parse(decodeURIComponent(bookingData));
              const bookingParams = new URLSearchParams(decodedBookingData).toString();
              router.replace(`/(app)/booking/lesson-request?${bookingParams}`);
            } catch (error) {
              console.error("Failed to decode booking data:", error);
              router.replace("/(app)/buy-credits");
            }
          } else {
            router.replace("/(app)/buy-credits");
          }
        },
      });
    }
  }, [success, router, returnTo, bookingData]);

  if (isLoadingChildren) {
    return <Loader />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      {childList.length > 0 && !!lessonPrices && !!selectedChildId && (
        <BuyCreditsForm
          lessonPrices={lessonPrices}
          childList={childList}
          selectedChildId={selectedChildId}
          setSelectedChildId={setSelectedChildId}
          returnTo={returnTo}
          bookingData={bookingData}
        />
      )}
    </View>
  );
}

export default BuyCreditsScreen;
